import axios from 'axios';
import Micromodal from 'micromodal';
import { useEffect, useState } from 'react';
import { getModalOption, Setting, Subscription } from '../App';
import { CUSTOMER_API_URL } from '../constants';
import { BoxDetailProductType } from '../types/view/BoxDetailType';

export type Image = {
  altText?: string;
  originalSrc?: string;
  transformedSrc: string;
};

export type Plan = {
  id: number;
  planId: string;
  name: string;
  description: string;
  firstPricingPolicyAdjustmentType: string;
  firstPricingPolicyAdjustmentValue: number;
  pricingPolicyAdjustmentType: string;
  pricingPolicyAdjustmentValue: number;
  readonly: boolean;
};

export type Product = {
  id: number;
  productId: string;
  title: string;
  handle: string;
  variants: {
    id: number;
    variantId: string;
    displayName: string;
  }[];
  quantity: number | null;
};

export type Box = {
  id?: number;
  title: string;
  description: string;
  pricingType: string;
  planGroupId: string;
  parentProduct: Product;
  boxVariants: {
    variantId: string;
    quantity: string;
  }[];
  childProducts: Product[];
  planGroup: {
    id: number;
    name: string;
    plans: Plan[];
  };
};

// @see use-subscription-detail.ts
type Variant = {
  id: string;
  displayName: string;
  title: string;
  sku: string;
  price: string;
  image?: Image;
  requiresShipping: boolean;
  availableForSale: boolean;
};
type ProductWithImage = {
  productId: string;
  title: string;
  images: Image[];
  variants: Variant[];
  onlineStorePreviewUrl: string;
};
type PlanGroupProduct = {
  merchantCode: string;
  name: string;
  planGroupId: string;
  plans: Plan[];
  products?: ProductWithImage[];
  boxes?: Box[];
};

export const useBoxDetail = (
  setting: Setting,
  subscriptionContractId: string,
  boxId: string,
  completion: (childProducts: BoxDetailProductType[], parentVariantId?: string) => void,
) => {
  const [subscription, setSubscription] = useState<Subscription>();
  const [box, setBox] = useState<Box>();
  const [isProp, setIsProp] = useState(false);
  const [planGroups, setPlanGroups] = useState<PlanGroupProduct[]>();
  const [subscriptionLinePlan, setSubscriptionLinePlan] = useState<
    {
      lineId: string;
      planId?: string;
      name?: string;
    }[]
  >([]);

  useEffect(() => {
    axios
      .post(
        `${CUSTOMER_API_URL}?action=getSubscription`,
        { subscriptionId: subscriptionContractId },
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        setSubscription({ ...response.data, disabled: false });
      });

    axios
      .post(
        `${CUSTOMER_API_URL}?action=getSubscriptionLinePlan`,
        {
          subscriptionId: subscriptionContractId,
        },
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        setSubscriptionLinePlan(response.data);
      });

    axios
      .post(
        `${CUSTOMER_API_URL}?action=getBox`,
        { boxId: boxId },
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        setBox(response.data);
      });

    axios
      .post(
        `${CUSTOMER_API_URL}?action=getProductDetails`,
        {
          box: true,
        },
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        setPlanGroups(response.data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!box || !subscription || !planGroups) return;

    const planGroup = planGroups.find((pg) => {
      return pg.boxes?.some((b) => b.id === box?.id);
    });

    // 選択中の親商品
    const parentVariantId = subscription.subscriptionLines.find(
      (sl) => sl.productId === box.parentProduct.productId,
    )?.variantId;

    // 選択商品
    const selectedProducts = box.childProducts.flatMap((product) => {
      // 商品無効化の状態は box からは取れないのでプラングループの商品を参照する
      const planGroupProduct = planGroup?.products?.find((p) => p.productId === product.productId);
      return product.variants
        .filter((v) => planGroupProduct?.variants.some((_v) => _v.id === v.variantId))
        .map((v) => {
          const variant = planGroupProduct?.variants.find((_v) => _v.id === v.variantId)!;
          const quantity = subscription.subscriptionLines.find((sl) => sl.variantId === variant.id)?.quantity;

          return {
            productId: product.id,
            variantId: Number(variant.id.split('/').reverse()[0]),
            displayName: variant.displayName,
            handle: product.title,
            image: {
              altText: variant.image?.altText,
              originalSrc: variant.image?.originalSrc ?? planGroupProduct?.images[0]?.originalSrc,
            },
            quantity: quantity ?? 0,
            onlineStorePreviewUrl: planGroupProduct?.onlineStorePreviewUrl,
          };
        });
    });

    completion(selectedProducts, parentVariantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box, subscription, planGroups]);

  const requestUpdate = async (parentVariantId: string, childProducts: BoxDetailProductType[]) => {
    if (!subscription || !box || !planGroups) {
      return;
    }

    const boxPlanGroup = planGroups.find((pg) => {
      return pg.boxes?.some((b) => b.id === box?.id);
    });

    setIsProp(true);
    try {
      // 親商品の更新（数量）
      const parentSubscriptionLine = subscription.subscriptionLines.find(
        (line) => line.productId === box.parentProduct.productId && line.variantId !== parentVariantId,
      );

      if (parentSubscriptionLine) {
        await axios.post(
          `${CUSTOMER_API_URL}?action=removeSubscriptionLine`,
          {
            subscriptionId: subscriptionContractId,
            subscriptionLine: {
              lineId: parentSubscriptionLine?.lineId,
            },
          },
          {
            headers: { Authorization: setting.token },
          },
        );

        const parentProduct = boxPlanGroup?.products?.find((p) => p.productId === parentSubscriptionLine?.productId);
        const parentVariant = parentProduct?.variants.find((v) => v.id === parentVariantId);
        const planId = subscriptionLinePlan.find((line) => line.lineId === parentSubscriptionLine.lineId)?.planId;
        const plan = box.planGroup.plans.find((x) => x.planId === planId);

        await axios.post(
          `${CUSTOMER_API_URL}?action=addSubscriptionLine`,
          {
            subscriptionId: subscriptionContractId,
            planId: plan?.planId,
            planName: plan?.name,
            subscriptionLine: {
              onlineStorePreviewUrl: parentProduct?.onlineStorePreviewUrl,
              productId: parentProduct?.productId,
              variantId: parentVariant!.id,
              variantImage: parentVariant!.image ? parentVariant!.image!.transformedSrc : null,
              title: parentProduct?.title,
              variantTitle: parentVariant?.title,
              quantity: parentSubscriptionLine.quantity,
              currentPriceAmount: parentVariant!.price,
              currentPriceCurrencyCode: 'JPY',
              lineDiscountedPriceAmount: parentVariant!.price, // api で計算される
              lineDiscountedPriceCurrencyCode: 'JPY',
              sku: parentVariant!.sku,
              lineId: parentSubscriptionLine?.lineId,
            },
          },
          {
            headers: { Authorization: setting.token },
          },
        );
      }

      // 新たに追加された
      const addChildProducts = childProducts.filter((childProduct) => {
        return (
          Number(childProduct.quantity ?? 0) > 0 &&
          !subscription.subscriptionLines.some(
            (line) => line.variantId === `gid://shopify/ProductVariant/${childProduct.variantId}`,
          )
        );
      });

      for (const childProduct of addChildProducts) {
        const variant = boxPlanGroup?.products?.flatMap((p) =>
          p.variants.filter((v) => v.id === `gid://shopify/ProductVariant/${childProduct.variantId}`),
        )[0];
        // 追加するプランを特定するために、「親」ではない subscriptionLine を取得
        const childSubscriptionLine = subscription.subscriptionLines.find(
          (line) => line.productId !== box.parentProduct.productId,
        );

        const planId = subscriptionLinePlan.find((line) => line.lineId === childSubscriptionLine?.lineId)?.planId;
        const plan = box.planGroup.plans.find((x) => x.planId === planId);

        await axios.post(
          `${CUSTOMER_API_URL}?action=addSubscriptionLine`,
          {
            subscriptionId: subscriptionContractId,
            planId: plan?.planId,
            planName: plan?.name,
            subscriptionLine: {
              onlineStorePreviewUrl: childProduct.onlineStorePreviewUrl,
              productId: `gid://shopify/Product/${childProduct.productId}`,
              variantId: variant!.id,
              variantImage: variant!.image ? variant!.image!.transformedSrc : null,
              title: childProduct.handle,
              variantTitle: variant!.title,
              quantity: childProduct.quantity,
              currentPriceAmount: variant!.price,
              currentPriceCurrencyCode: 'JPY',
              lineDiscountedPriceAmount: variant!.price,
              lineDiscountedPriceCurrencyCode: 'JPY',
              sku: variant!.sku,
              lineId: null,
            },
          },
          {
            headers: { Authorization: setting.token },
          },
        );
      }

      // 数量が変更された
      const updateSubscriptionLines = subscription.subscriptionLines.filter((line) => {
        const quantity = childProducts.find(
          (childProduct) => `gid://shopify/ProductVariant/${childProduct.variantId}` === line.variantId,
        )?.quantity;

        if (!quantity) {
          return false;
        }

        return line.quantity !== quantity;
      });

      for (const line of updateSubscriptionLines) {
        const quantity = childProducts.find(
          (childProduct) => `gid://shopify/ProductVariant/${childProduct.variantId}` === line.variantId,
        )?.quantity;

        await axios.post(
          `${CUSTOMER_API_URL}?action=updateSubscriptionLine`,
          {
            subscriptionId: subscriptionContractId,
            lineId: line.lineId,
            quantity,
          },
          {
            headers: { Authorization: setting.token },
          },
        );
      }

      // 選択から外された(0 になった)
      const removeChildProducts = childProducts.filter((childProduct) => {
        return (
          Number(childProduct.quantity ?? 0) === 0 &&
          subscription.subscriptionLines.some(
            (line) => line.variantId === `gid://shopify/ProductVariant/${childProduct.variantId}`,
          )
        );
      });

      for (const childProduct of removeChildProducts) {
        // 削除対象 line の特定
        const subscriptionLine = subscription.subscriptionLines.find(
          (line) => line.variantId === `gid://shopify/ProductVariant/${childProduct.variantId}`,
        );

        await axios.post(
          `${CUSTOMER_API_URL}?action=removeSubscriptionLine`,
          {
            subscriptionId: subscriptionContractId,
            subscriptionLine: {
              lineId: subscriptionLine?.lineId,
            },
          },
          {
            headers: { Authorization: setting.token },
          },
        );
      }

      Micromodal.show('modal-complete', getModalOption(setting));
    } catch (e) {
      Micromodal.show('modal-error', getModalOption(setting));
    }
    setIsProp(false);
  };

  return {
    box,
    requestUpdate,
    isProp,
  };
};
